 
 
import React, { Fragment, ReactElement, useEffect, useState } from 'react';

import {
  ButtonPattern,
  Container,
  Grid,
  GridItem,
  List,
  ListItem,
  Notification,
  Paragraph,
  Text,
} from '@constellation/core';
import { useContent } from '@interstellar/react-app-content';
import { useNavigate } from '@interstellar/react-app-routing';
import PageHeading from 'components/pageHeading/PageHeading';
import { StyledLink } from 'components/registrationSteps/RegistrationSteps.styled';
import StyledBox from 'components/style/styledBox/StyledBox';
import { Button, Link } from 'components/tagging';
import { useForm } from 'react-hook-form';

import { AboutYouContent } from './AboutYouContent';
import AboutYouInputs from './aboutYouInputs/AboutYouInputs';
import AboutYouNotification from './aboutYouNotification/AboutYouNotification';
import NeedHelp from './needHelp/NeedHelp';
import { getIndividualDetails } from '../../api';
import {
  BUTTON_EVENTS,
  JOURNEY_STEPS,
} from '../../constants/analytics.constants';
import { useGlobalProvider } from '../../context/GlobalContext';
import * as Analytics from '../../utils/analytics';
import {
  NO_OCIS_FOUND_CODE,
  MANDATE_EXIST_CODE,
  REGISTRATION_IN_PROGRESS_ERROR_CODE,
  INDIVIDUAL_PHONE_NUMBER_NOT_FOUND,
  RETRIEVE_PARTY_DETAILS_FAILURE,
} from '../../utils/constant';
import { AboutYouFormFields } from '../../utils/types';

// This component will render when the user is a full power signatory
function AboutYou(): ReactElement {
  const [isNoOcisMatchFound, setIsNoOcisMatchFound] = useState<boolean>(false);
  const { aboutYouContent } = useContent<AboutYouContent>();
  const { globalState, setGlobalContext } = useGlobalProvider();
  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<AboutYouFormFields>({ reValidateMode: 'onSubmit' });
  const navigate = useNavigate();

  // If all inputs are correect navigate to next page
  const handleContinue = async (submittedData): Promise<void> => {
    setIsNoOcisMatchFound(false);
    const body = {
      individual: {
        firstName: submittedData.firstName,
        lastName: submittedData.lastName,
        dateOfBirth: `${submittedData.dateOfBirth.year}-${submittedData.dateOfBirth.month}-${submittedData.dateOfBirth.day}`,
        email: submittedData.emailAddress,
      },
      organisation: {
        partyId: globalState?.partyId,
      },
    };

    try {
      const response = await getIndividualDetails(body);
      if (response.status === 200) {
        setGlobalContext({
          customerDetails: response.data,
        });
        navigate(aboutYouContent[0].nextBtnHref, {
          state: { userData: response.data },
        });
      }
    } catch (err) {
      const error: any = err?.response?.data?.errors?.[0];
      const reasonCode: string = error?.reasonCode;
      if (
        reasonCode === RETRIEVE_PARTY_DETAILS_FAILURE ||
        reasonCode === NO_OCIS_FOUND_CODE
      ) {
        setIsNoOcisMatchFound(true);
        Analytics.setPageLoadEvent(
          JOURNEY_STEPS.ONLINE_REGISTRATION.ABOUT_YOU_ERROR,
        );
      } else if (reasonCode === REGISTRATION_IN_PROGRESS_ERROR_CODE) {
        // Get PEGA Case ID
        const parts = error?.message?.split(':');
        const PegaCaseId = parts[1].trim();
        navigate(aboutYouContent[0].registrationInProgressPath, {
          state: { PegaCaseId },
        });
      } else if (reasonCode === MANDATE_EXIST_CODE) {
        const [{ alreadyRegisteredPagePath }] = aboutYouContent;
        navigate(alreadyRegisteredPagePath);
      } else if (reasonCode === INDIVIDUAL_PHONE_NUMBER_NOT_FOUND) {
        const [{ mobileNumberMissingPagePath }] = aboutYouContent;
        navigate(mobileNumberMissingPagePath);
      } else {
        // for any other responses, navigate to technical error page
        navigate(aboutYouContent[0].technicalErrorPageHref);
      }
    }
  };

  // When field level errors exist, hide the error notification for OCIS data not found
  useEffect(() => {
    setIsNoOcisMatchFound(false);
  }, [
    errors.firstName,
    errors.lastName,
    errors.dateOfBirth,
    errors.emailAddress,
    errors.confirmYourEmailAddress,
    errors.agreeToTermsAndConditions,
  ]);

  useEffect(() => {
    Analytics.setPageLoadEvent(JOURNEY_STEPS.ONLINE_REGISTRATION.ABOUT_YOU);
  }, []);

  // Focussing the error summary when No OCIS match found error is thrown from the api
  useEffect(() => {
    const errorNotificationElement = document.querySelector(
      '.csl-notification--critical',
    );
    // Focussing on the error notification
    if (errorNotificationElement) {
      errorNotificationElement.setAttribute('tabindex', '0');
      (errorNotificationElement as HTMLElement).focus();
    }
  }, [isNoOcisMatchFound]);

  const handleNavigation = (path: string): void => {
    navigate(path);
  };

  return (
    <Container padding="none">
      {aboutYouContent &&
        aboutYouContent.map(
          ({
            serviceName,
            header,
            errorHeading,
            notification,
            minorUserError,
            nextBtn,
            previousHref,
            previousLink,
            noOCISMatchFound,
          }) => (
            <Fragment key="about-you">
              <PageHeading serviceName={serviceName} header={header} />
              <Grid alignX="center" alignY="top">
                <GridItem lg={8}>
                  <StyledBox>
                    {!Object.keys(errors).length || (
                      <AboutYouNotification
                        errors={errors}
                        errorHeading={errorHeading}
                        minorUserError={minorUserError}
                      />
                    )}
                    <form onSubmit={handleSubmit(handleContinue)} noValidate>
                      {isNoOcisMatchFound && (
                        <Notification
                          sentiment="critical"
                          heading={noOCISMatchFound.heading}
                        >
                          {noOCISMatchFound.content1}
                          <List>
                            <ListItem>{noOCISMatchFound.listItem1}</ListItem>
                            <ListItem>{noOCISMatchFound.listItem2}</ListItem>
                          </List>
                          <Paragraph size="s2" marginBottom="03">
                            {noOCISMatchFound.content2}{' '}
                            <Link
                              href={`tel:${noOCISMatchFound.contactNo}`}
                              weight="bold"
                              event={`JourneyEvent: 'Click to Call', EventAction: 'Click to Call', EventNarrative: ${noOCISMatchFound.contactNo}`}
                            >
                              <Text weight="bold" color="brand">
                                {noOCISMatchFound.contactNo}
                              </Text>
                            </Link>{' '}
                            {noOCISMatchFound.content3}
                          </Paragraph>
                        </Notification>
                      )}
                      {errors &&
                        errors.dateOfBirth &&
                        errors.dateOfBirth.message === minorUserError && (
                          <Notification sentiment="critical">
                            {minorUserError}
                          </Notification>
                        )}
                      <Notification>{notification}</Notification>
                      <AboutYouInputs
                        errors={errors}
                        register={register}
                        setValue={setValue}
                        control={control}
                        watch={watch}
                      />
                      <ButtonPattern marginBottom="none" marginTop="07">
                        <Button
                          variation="primary"
                          iconPosition="right"
                          type="submit"
                          taggingLabel={nextBtn}
                        >
                          {nextBtn}
                        </Button>
                        <StyledLink
                          iconPosition="left"
                          onClick={() => handleNavigation(previousHref)}
                          event={BUTTON_EVENTS.PREVIOUS}
                        >
                          {previousLink}
                        </StyledLink>
                      </ButtonPattern>
                    </form>
                  </StyledBox>
                </GridItem>
                <GridItem lg={4}>
                  {/* FAQs accordian */}
                  <NeedHelp />
                </GridItem>
              </Grid>
            </Fragment>
          ),
        )}
    </Container>
  );
}

export default AboutYou;
